import React from 'react';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children: React.ReactNode;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className = '', ...props }, ref) => {
    const baseStyles = 'bg-rm-bg-50 rounded-card shadow-card';
    const classes = [baseStyles, className].filter(Boolean).join(' ');

    return (
      <div ref={ref} className={classes} {...props} />
    );
  }
);

Card.displayName = 'Card';

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children: React.ReactNode;
}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className = '', ...props }, ref) => {
    const baseStyles = 'flex flex-col space-y-1.5 p-6';
    const classes = [baseStyles, className].filter(Boolean).join(' ');

    return (
      <div ref={ref} className={classes} {...props} />
    );
  }
);

CardHeader.displayName = 'CardHeader';

interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  className?: string;
  children: React.ReactNode;
}

const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className = '', children, ...props }, ref) => {
    const baseStyles = 'text-2xl font-semibold text-rm-primary-700 leading-none tracking-tight';
    const classes = [baseStyles, className].filter(Boolean).join(' ');

    return (
      <h3 ref={ref} className={classes} {...props}>
        {children}
      </h3>
    );
  }
);

CardTitle.displayName = 'CardTitle';

interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  className?: string;
  children: React.ReactNode;
}

const CardDescription = React.forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className = '', ...props }, ref) => {
    const baseStyles = 'text-sm text-rm-neutral-600';
    const classes = [baseStyles, className].filter(Boolean).join(' ');

    return (
      <p ref={ref} className={classes} {...props} />
    );
  }
);

CardDescription.displayName = 'CardDescription';

interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children: React.ReactNode;
}

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className = '', ...props }, ref) => {
    const baseStyles = 'p-6 pt-0 text-rm-ink-900';
    const classes = [baseStyles, className].filter(Boolean).join(' ');

    return (
      <div ref={ref} className={classes} {...props} />
    );
  }
);

CardContent.displayName = 'CardContent';

interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children: React.ReactNode;
}

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className = '', ...props }, ref) => {
    const baseStyles = 'flex items-center p-6 pt-0';
    const classes = [baseStyles, className].filter(Boolean).join(' ');

    return (
      <div ref={ref} className={classes} {...props} />
    );
  }
);

CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
export type { CardProps, CardHeaderProps, CardFooterProps, CardTitleProps, CardDescriptionProps, CardContentProps };
